import { Navigate, useLocation } from "react-router-dom";
import { type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { useUserOrganizations } from "@/lib/org";
import { useTenantIdentity } from "@/lib/useTenantIdentity";
import { LoadingScreen } from "@/components/shell/LoadingScreen";

interface ProtectedRouteProps {
  children: ReactNode;
  requireOrg?: boolean;
}

export function ProtectedRoute({
  children,
  requireOrg = true,
}: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const {
    data: memberships,
    isLoading: orgsLoading,
    isFetching: orgsFetching,
    isSuccess: orgsLoaded,
  } = useUserOrganizations();
  const {
    data: tenant,
    isLoading: tLoading,
    isFetching: tFetching,
    isSuccess: tLoaded,
  } = useTenantIdentity();
  const location = useLocation();

  if (authLoading) return <LoadingScreen />;
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (requireOrg) {
    // Wait for the query to actually finish before deciding.
    // isLoading covers initial load, isFetching covers refetches,
    // and we explicitly wait for isSuccess before reading data.
    if (orgsLoading || orgsFetching || !orgsLoaded) {
      return <LoadingScreen />;
    }
    // Now memberships is guaranteed to be defined (could still be empty array).
    if (!memberships || memberships.length === 0) {
      // No org membership. Before assuming "new manager -> onboarding",
      // check whether this is a linked tenant. A tenant has no org row and
      // must never see the manager org-setup form. Wait for the tenant query
      // to settle so we never bounce a tenant to onboarding on a race.
      if (tLoading || tFetching || !tLoaded) {
        return <LoadingScreen />;
      }
      if (tenant) {
        return <Navigate to="/tenant" replace />;
      }
      return <Navigate to="/onboarding" replace />;
    }
  }
  return <>{children}</>;
}